import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../utils/helpers";
import Skeleton from "./Skeleton";
import "./AuditChangeReport.css";

const ACTIVE = new Set(["pending", "waiting", "generating"]);
const MEMORY_ACTIVE = new Set(["pending", "queued"]);
const MEMORY_STATES = new Set([...MEMORY_ACTIVE, "stored", "disabled", "blocked", "suppressed", "unconfirmed", "unavailable"]);
const REPORT_STATES = new Set([...ACTIVE, "ready", "failed"]);
const TRANSPORT_STATES = new Set(["pending", "running", "completed", "failed", "cancelled", "unknown"]);
const RESULT_STATES = new Set(["unknown", "succeeded", "failed", "skipped"]);
const MAX_POLLS = 20;

export function safeReportUrl(value) {
  if (typeof value !== "string" || !/^https?:\/\//i.test(value)) return null;
  try {
    const url = new URL(value);
    return !url.username && !url.password ? url.href : null;
  } catch { return null; }
}

export function renderReportMarkdown(markdown) {
  const html = marked.parse(String(markdown || "").slice(0, 200000), { async: false, gfm: true });
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "del", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote", "pre", "code", "hr", "table", "thead", "tbody", "tr", "th", "td", "a"],
    ALLOWED_ATTR: ["href", "title", "colspan", "rowspan"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll("a").forEach(link => {
    const href = safeReportUrl(link.getAttribute("href"));
    if (!href) link.replaceWith(...link.childNodes);
    else {
      link.setAttribute("href", href);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
      link.setAttribute("referrerpolicy", "no-referrer");
    }
  });
  const container = document.createElement("div");
  container.appendChild(fragment);
  return container.innerHTML;
}

function Report({ entryId, onAuditStatus }) {
  const { t, i18n } = useTranslation(["auditlog", "gemeinsam"]);
  const [report, setReport] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retryError, setRetryError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const mounted = useRef(false);
  const retryController = useRef(null);
  const auditStatusCallback = useRef(onAuditStatus);
  auditStatusCallback.current = onAuditStatus;
  const path = `/api/audit-log/${encodeURIComponent(entryId)}/change-report`;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; retryController.current?.abort(); };
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    let polls = 0;
    const controller = new AbortController();
    setLoadError(null);
    setPaused(false);
    const load = async () => {
      try {
        const data = await api(path, { signal: controller.signal });
        if (!active) return;
        if (!data || !REPORT_STATES.has(data.status)) throw new Error("invalid_response");
        setReport(data);
        setLoadError(null);
        if (["pending", "error", "ok", "warning"].includes(data.auditStatus)) auditStatusCallback.current?.(entryId, data.auditStatus);
        if (ACTIVE.has(data.status) || MEMORY_ACTIVE.has(data.memory?.status)) {
          if (++polls >= MAX_POLLS) setPaused(true);
          else timer = setTimeout(load, Math.min(5000 * Math.pow(1.5, polls - 1), 30000));
        }
      } catch (error) {
        if (active) setLoadError(error.status === 403 ? "forbidden" : error.status === 404 ? "missing" : "load");
      }
    };
    load();
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [path, refresh, entryId]);

  const retry = async () => {
    if (retryController.current || retrying || report?.canRetry !== true) return;
    const controller = new AbortController();
    retryController.current = controller;
    setRetrying(true);
    setRetryError(null);
    try {
      await api(`${path}/retry`, { method: "POST", body: JSON.stringify({}), signal: controller.signal });
      if (!mounted.current) return;
      setReport(previous => ({ ...previous, status: "pending", markdown: null, errorCode: null }));
      setRefresh(value => value + 1);
    } catch (error) {
      if (mounted.current) setRetryError(error.status === 403 ? "forbidden" : "retry");
    } finally {
      retryController.current = null;
      if (mounted.current) setRetrying(false);
    }
  };

  const html = useMemo(() => renderReportMarkdown(report?.markdown), [report?.markdown]);
  const transport = report?.transport;
  const comparison = report?.comparison;
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "de-DE";
  const date = value => {
    const parsed = new Date(value);
    return value && Number.isFinite(parsed.getTime()) ? parsed.toLocaleString(locale) : null;
  };
  const generatedAt = date(report?.generatedAt);
  const capturedAt = date(comparison?.capturedAt);
  const runUrl = safeReportUrl(transport?.runUrl);
  const artifactStatus = value => t(`bericht.ergebnis.${RESULT_STATES.has(value) ? value : "unknown"}`);
  const busy = !loadError && (!report || ACTIVE.has(report.status)) && !paused;
  const errorKeys = {
    ACCESS_REVOKED: "forbidden", PROTECTED_RESOURCE: "protected", LLM_NOT_CONFIGURED: "configuration",
    RUN_NOT_FOUND: "runMissing", RECEIPT_MISMATCH: "receipt", EMPTY_REPORT: "empty",
    REPORT_GENERATION_FAILED: "generation",
  };

  return <section className="iio-adt-report" aria-label={t("bericht.titel")} aria-busy={busy || retrying}>
    <h3>{t("bericht.titel")}</h3>
    {loadError ? <div role="alert" className="iio-adt-report-message">
      <p>{t(`bericht.fehler.${loadError}`)}</p>
      <button type="button" className="iio-btn iio-btn--secondary" onClick={() => setRefresh(value => value + 1)}>{t("gemeinsam:aktion.aktualisieren")}</button>
    </div> : !report ? <Skeleton variant="lines" count={3} label={t("bericht.laden")} /> : <>
      <p role="status" aria-live="polite">{t(`bericht.status.${report.status}`)}</p>
      {report.memory && <p role="status" aria-live="polite">{t(`bericht.gedaechtnis.${MEMORY_STATES.has(report.memory.status) ? report.memory.status : "unavailable"}`,
        { stored: report.memory.stored, total: report.memory.total })}</p>}
      {transport && <div className="iio-adt-report-facts">
        <span>{t("bericht.transport")}: {t(`bericht.transportstatus.${TRANSPORT_STATES.has(transport.status) ? transport.status : "unknown"}`)}</span>
        {runUrl && <a href={runUrl} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{t("bericht.laufOeffnen")}</a>}
      </div>}
      {comparison && <div className="iio-adt-report-facts">
        {Number.isInteger(comparison.artifactCount) && <span>{t("bericht.artefakte", { count: comparison.artifactCount })}</span>}
        {Number.isInteger(comparison.changedFiles) && <span>{t("dateien.geaendert", { count: comparison.changedFiles })}</span>}
        {capturedAt && <span>{t("bericht.erfasst", { zeit: capturedAt })}</span>}
      </div>}
      {comparison?.sourceSha && <p className="iio-adt-report-reference">{t("bericht.gitStand", { sha: comparison.sourceSha })}</p>}
      {Array.isArray(comparison?.warnings) && comparison.warnings.length > 0 && <p>{t("bericht.eingeschraenkt")}</p>}
      {Array.isArray(transport?.artifacts) && transport.artifacts.length > 0 && <details className="iio-adt-report-results">
        <summary>{t("bericht.artefaktergebnisse")}</summary>
        <ul>{transport.artifacts.map((artifact, index) => <li key={`${artifact.id}-${index}`}>
          <strong>{artifact.id}</strong>
          <dl>
            <dt>{t("bericht.upload")}</dt><dd>{artifactStatus(artifact.upload)}</dd>
            <dt>{t("bericht.konfiguration")}</dt><dd>{artifactStatus(artifact.configuration)}</dd>
            <dt>{t("bericht.deployment")}</dt><dd>{artifactStatus(artifact.deployment)}</dd>
            {artifact.runtimeStatus && <><dt>{t("bericht.runtime")}</dt><dd>{artifact.runtimeStatus}</dd></>}
          </dl>
          {artifact.versionVerified !== true && <p>{t("bericht.versionUnbestaetigt")}</p>}
        </li>)}</ul>
      </details>}
      {report.status === "ready" && (report.markdown?.trim() && html.trim()
        ? <>
          <div className="iio-adt-report-markdown" lang={report.language === "en" ? "en" : "de"} dangerouslySetInnerHTML={{ __html: html }} />
          <p className="iio-adt-report-reference">{t("bericht.kiHinweis")}</p>
          {generatedAt && <p className="iio-adt-report-reference">{t("bericht.erstellt", { zeit: generatedAt })}{report.model ? ` · ${report.model}` : ""}</p>}
        </>
        : <p role="alert">{t("bericht.fehler.empty")}</p>)}
      {report.status === "failed" && <div className="iio-adt-report-message">
        <p role="alert">{t(`bericht.fehler.${errorKeys[report.errorCode] || "generation"}`)}</p>
        {report.canRetry === true && <>
          <button type="button" className="iio-btn iio-btn--secondary" disabled={retrying} onClick={retry}>{t(retrying ? "bericht.wiederholungLaeuft" : "bericht.wiederholen")}</button>
          <p>{t("bericht.wiederholungHinweis")}</p>
        </>}
      </div>}
      {retryError && <p role="alert">{t(`bericht.fehler.${retryError}`)}</p>}
      {paused && <div className="iio-adt-report-message">
        <p role="status">{t("bericht.abfragePausiert")}</p>
        <button type="button" className="iio-btn iio-btn--secondary" onClick={() => setRefresh(value => value + 1)}>{t("gemeinsam:aktion.aktualisieren")}</button>
      </div>}
    </>}
  </section>;
}

export default function AuditChangeReport({ entryId, reportId, open = true, onAuditStatus }) {
  if (!open || !entryId || !reportId) return null;
  return <Report key={`${entryId}:${reportId}`} entryId={entryId} onAuditStatus={onAuditStatus} />;
}
